import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../enviroment/enviroment';

// Interfaces para mejor tipado
interface ReservationAdditionalService {
  serviceId: string;
  name: string;
  cost: number;
  description: string;
}

interface ReservationData {
  checkInDate: string;
  checkOutDate: string;
  hotelId: string;
  roomId: string;
  roomCost: number;
  numAdults: number;
  numChildren: number;
  numInfants: number;
  additionalServices: ReservationAdditionalService[];
}

interface GuestData {
  name: string;
  lastname: string;
  identificationType: string;
  identificationNumber: string;
  phone: string;
  emergencyPhone: string;
  reservation_id: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReservationService {
  // 🎯 URLs consolidadas según la nueva estructura del backend
  private readonly API_BASE_URL = environment.apiUrl;


  // URLs principales para reservas
  private readonly reservationsUrl = `${this.API_BASE_URL}/reservations`; // POST: Crear, GET: Consultar, DELETE: Cancelar
  private readonly calculationsUrl = `${this.API_BASE_URL}/calculations`; // POST: Crear, GET: Consultar, DELETE: Cancelar
  private readonly startPaymentUrl = `${this.API_BASE_URL}/payments/start`; // POST: Registrar huéspedes
  private readonly mercadoPagoUrl = environment.mercadoPagoUrl; // POST: Generar link de pago
  private readonly userReservationsUrl = `${this.API_BASE_URL}/userReservations`; // GET: Ver reservas del usuario
  private readonly guestsUrl = `${this.API_BASE_URL}/payments/start`; // PUT: Actualizar huéspedes


  // URLs para endpoints adicionales (webhooks, etc.)
  private readonly endingPaymentUrl = environment.endingPaymentUrl; // Webhook de MercadoPago

  constructor(private readonly http: HttpClient) { }


  getAllReservations(params: { page: number; limit: number, user_id: string }, accessToken: string | null): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });

    const httpParams = new HttpParams()
      .set('page', params.page)
      .set('limit', params.limit)
      .set('user_id', params.user_id);

    return this.http.get(this.reservationsUrl, {
      headers,
      params: httpParams
    });
  }


  getAllReservationsRecep(params: { page: number; limit: number, user_id: string, alojamiento:string }, accessToken: string | null): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });

    const httpParams = new HttpParams()
      .set('page', params.page)
      .set('limit', params.limit)
      .set('user_id', params.user_id)
       .set('hotel_id', params.alojamiento);
    return this.http.get(this.reservationsUrl, {
      headers,
      params: httpParams
    });
  }
  // ===== PASO 1: CREAR RESERVA INICIAL (calculations endpoint) =====

  /**
 * Crear una nueva reserva (endpoint /calculations)
 * - Crea la reserva con estado: "Pendiente de pago"
 * - Crea un registro de pago con estado: "Pendiente de pago"
 * - Retorna reservation_id y detalles de costos
 */

  /**
 * Crear una nueva reserva (endpoint /calculations)
 * - Crea la reserva con estado: "Pendiente de pago"
 * - Crea un registro de pago con estado: "Pendiente de pago"
 * - Retorna reservation_id y detalles de costos
 */
  createReservation(reservationData: ReservationData, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    // Usar el endpoint correcto según el flujo del backend
    return this.http.post(this.reservationsUrl, reservationData, { headers });
  }

  updateReservation(reservationData: any, accessToken: string, reservation_id: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.put(`${this.reservationsUrl}/${reservation_id}`, reservationData, { headers });
  }

  /**
   * Obtener detalles de una reserva específica
   * - Público: Solo información básica
   * - Con auth: Información completa del usuario
   */
  getReservation(reservation_id: string, accessToken?: string): Observable<any> {
    const headers = accessToken ? new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    }) : undefined;

    return this.http.get(`${this.reservationsUrl}?reservation_id=${reservation_id}`,
      headers ? { headers } : undefined
    );
  }

  /**
   * Cancelar reserva (requiere autenticación)
   */
  deleteReservation(reservation_id: string, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.delete(`${this.reservationsUrl}/${reservation_id}`, { headers });
  }

  // ===== PASO 2: REGISTRAR HUÉSPEDES (startPayment endpoint) =====


  /**
   * PASO 2: Registrar huéspedes asociados a una reserva
   * - Registra los huéspedes
   * - Cambia estado del pago a: "Reserva confirmada - Pendiente de pago"
   */
  addGuests(guests: GuestData[]): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.post(this.startPaymentUrl, guests, { headers });
  }

  /**
   * Consultar huéspedes de una reserva
   */
  getGuests(reservation_id: string, accessToken: string): Observable<any> {
    console.log('🔍 [GET-GUESTS] Iniciando consulta de huéspedes:', {
      reservation_id,
      tokenLength: accessToken?.length,
      tokenStart: accessToken?.substring(0, 20),
      tokenEnd: accessToken?.substring(accessToken.length - 20)
    });

    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim() === '') {
      console.error('❌ [GET-GUESTS] Token de acceso inválido o vacío');
      throw new Error('Token de acceso requerido para consultar huéspedes');
    }

    // Validar formato JWT básico
    if (!/^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/.test(accessToken)) {
      console.error('❌ [GET-GUESTS] Token con formato JWT inválido:', accessToken);
      throw new Error('Token de acceso con formato inválido');
    }

    // Decodificar JWT para ver su contenido y expiración
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp < now;

      console.log('🔍 [GET-GUESTS] Información del token:', {
        sub: payload.sub,
        exp: payload.exp,
        iat: payload.iat,
        now: now,
        isExpired: isExpired,
        groups: payload['cognito:groups'],
        expiresIn: payload.exp - now + ' segundos'
      });

      if (isExpired) {
        console.error('❌ [GET-GUESTS] Token expirado!');
        throw new Error('Token expirado');
      }
    } catch (e) {
      console.error('❌ [GET-GUESTS] Error decodificando token:', e);
    }

    const authHeader = `Bearer ${accessToken}`;
    console.log('🔧 [GET-GUESTS] Creando headers:', {
      authHeaderLength: authHeader.length,
      authHeaderStart: authHeader.substring(0, 30),
      authHeaderEnd: authHeader.substring(authHeader.length - 30)
    });

    const headers = new HttpHeaders({
      'Authorization': authHeader
    });

    const url = `${this.startPaymentUrl}?reservation_id=${reservation_id}`;
    console.log('🌐 [GET-GUESTS] URL de la petición:', url);
    console.log('📋 [GET-GUESTS] Headers finales:', headers.get('Authorization'));

    return this.http.get(url, { headers });
  }

  /**
   * Actualizar información de huéspedes
   * Actualiza un huésped individual usando su guest_id
   */
  updateGuest(guestData: any, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    // URL individual por huésped
    const url = `${this.guestsUrl}/${guestData.guest_id}`;

    // Payload con los nombres de campos correctos
    const payload = {
      name: guestData.name,
      lastname: guestData.lastname,
      identificationType: guestData.identificationType,
      identificationNumber: guestData.identificationNumber,
      phone: guestData.phone,
      emergencyPhone: guestData.emergencyPhone
    };

    console.log('🔄 Actualizando huésped:', url, payload);
    return this.http.put(url, payload, { headers });
  }

  /**
   * Eliminar huéspedes
   */
  deleteGuests(guest_ids: string[]): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    return this.http.delete(this.startPaymentUrl, {
      headers,
      body: { guest_ids }
    });
  }

  // ===== MÉTODOS PARA DASHBOARD USUARIO (userReservations lambda) =====

  /**
   * Obtener todas las reservas del usuario con resumen
   */
  getUserReservations(accessToken: string, userId: string, includeGuests?: boolean, status?: string, hotelId?:string): Observable<any> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${accessToken}`
    });

    let params = new URLSearchParams();
    params.set('user_id', userId);
    if (includeGuests) params.set('include_guests', 'true');
    if (status) params.set('status', status);
    if(hotelId) params.set('hotel_Id', hotelId);

    const url = params.toString() ?
      `${this.userReservationsUrl}?${params.toString()}` :
      this.userReservationsUrl;

    return this.http.get(url, { headers });
  }

  // ===== PASO 3: GENERAR LINK DE PAGO (mercadoPago endpoint) =====

  /**
   * PASO 3: Generar link de pago de MercadoPago
   * - Crea preferencia en MercadoPago
   * - Cambia estado a: "Esperando pago"
   * - Retorna init_point (URL de pago)
   */
  createPaymentLink(reservation_id: string, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.post(this.mercadoPagoUrl, { reservation_id, source: 'ecoturismo' }, { headers });
  }

  // ===== PASO 4: VERIFICAR ESTADO DE PAGO (endingPayment endpoint) =====

  /**
   * PASO 4: Consultar estado de pago de una reserva
   * - Este endpoint recibe notificaciones automáticas del webhook de MercadoPago
   * - Estado final: "Pagado" o "Pago rechazado"
   * - También se puede consultar manualmente
   */
  getPaymentStatus(reservation_id: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const url = `${this.endingPaymentUrl}?id=${reservation_id}&topic=payment`;
    console.log('📡 Consultando estado de pago URL:', url);

    return this.http.post(url, { headers });
  }

  /**
   * Método mejorado para obtener reservas de un hosting específico
   * Usa el endpoint moderno de userReservations con filtros
   */
  getHostingReservations(hotelId: string, accessToken?: string, silent: boolean = true): Observable<any> {
    if (accessToken) {
      // Si tenemos token, usar el endpoint autenticado
      const headers = new HttpHeaders({
        'Authorization': `Bearer ${accessToken}`
      });

      const role = (() => {
        try {
          const { groups } = JSON.parse(localStorage.getItem("user") || "{}");
          return Array.isArray(groups) && (groups.includes("admin-hotel") || groups.includes("superadmin")) ? "admin" : "user";
        } catch {
          return "user";
        }
      })();

      return this.http.get(`${this.userReservationsUrl}?hosting_id=${hotelId}&role=${role}`, { headers });
    } else {
      // Si no tenemos token, usar endpoint público para fechas ocupadas (cuando esté disponible)
      // Por ahora simular respuesta vacía hasta que el backend implemente el endpoint público
      if (!silent) {
        console.warn('🚧 Sin token de acceso - usando respuesta simulada para fechas ocupadas');
        console.warn('📝 NOTA: Se requiere endpoint público en el backend para consultar fechas ocupadas sin autenticación');
      }
      return new Observable(observer => {
        observer.next({ reservations: [] });
        observer.complete();
      });
    }
  }

  updateReservationPaymentStatusAndPaymentMethod(reservation_id: string, payment_id: string, payment_method: string, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.put(`${this.calculationsUrl}/reservations/${reservation_id}/payments/${payment_id}`, { payment_method }, { headers });
  }

  getReservationDocument(reservation_id: string, type: string, accessToken: string, payment_id?: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });
    if (type !== 'identidad') {
      return this.http.get(`${this.calculationsUrl}/reservations/${reservation_id}/get-document?type=${type}&paymentId=${payment_id}`, { headers });
    }
    return this.http.get(`${this.calculationsUrl}/reservations/${reservation_id}/get-document?type=${type}`, { headers });
  }

  startStay(reservation_id: string, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.put(`${this.calculationsUrl}/reservations/${reservation_id}`, { status: 'Iniciada' }, { headers });
  }

  getStays(alojamientosStr: string, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.get(`${this.calculationsUrl}/reservations?status=iniciada&hotelIds=${alojamientosStr}`, { headers });
  }

  agregarProductosAEstadia(productos: any[], accessToken: string) {
    return this.http.post(`${this.calculationsUrl}/estadias/productos`, productos, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    });
  }

  getProductosDeEstadia(reservationId: string, accessToken: string) {
    return this.http.get(
      `${this.calculationsUrl}/estadias/productos?reservationId=${reservationId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
  }

  pagarProductos(reservationId: string, productIds: string[], metodoPago: string, accessToken: string) {
    return this.http.put(
      `${this.calculationsUrl}/estadias/productos/pagar?reservationId=${reservationId}`,
      { productIds, metodoPago },
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
  }

  endStay(reservation_id: string, accessToken: string): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    return this.http.put(`${this.calculationsUrl}/reservations/${reservation_id}`, { status: 'Finalizada' }, { headers });
  }

}
